import i18n from "@/i18n.ts";
import { ApiMeEmployee } from "@/types/employees/employee.types.ts";
import { ApiPermission, EmployeeFieldPermission, PermissionType } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";

export interface SettingGroup {
  title: string;
  items: SettingItem[];
}

export interface SettingItem {
  title: string;
  description: string;
  to: string;
  icon: string;
  menuTitle: string;
  anyOfPermissions?: ApiPermission[];
  allOfPermissions?: ApiPermission[];
  planFeature?: ApiFeatureType;
}

export interface SettingsItem {
  title: string;
  description: string;
  to: string;
  icon: string;
  allOfPermissions?: ApiPermission[];
  anyOfPermissions?: ApiPermission[];
  allOfEditFieldPermissions?: EmployeeFieldPermission[];
  planFeature?: ApiFeatureType;
  activePaths?: string[];
}

export interface SettingsGroup {
  key: string;
  title: string;
  description: string;
  icon: string;
  items: SettingsItem[];
}

export interface SettingsCategory {
  title: string;
  groups: SettingsGroup[];
}

export interface SettingsPersonalLink {
  title: string;
  description: string;
  icon: string;
  to: string;
}

export const getSettingsNavigation = (): SettingsCategory[] => [
  {
    title: i18n.t("common:company"),
    groups: [
      {
        key: "general",
        title: i18n.t("common:general"),
        description: i18n.t("core:settings_general_description"),
        icon: "fa-screwdriver-wrench",
        items: [
          {
            title: i18n.t("core:organization"),
            description: i18n.t("core:settings_organization_description"),
            to: "/settings/organization",
            icon: "fa-gear",
            allOfPermissions: [ApiPermission.settings_organization],
          },
          {
            title: i18n.t("core:features"),
            description: i18n.t("core:settings_features_description"),
            to: "/settings/features",
            icon: "fa-sliders-simple",
            allOfPermissions: [ApiPermission.settings_organization],
          },
          {
            title: i18n.t("core:organization_view_filter_title"),
            description: i18n.t("core:organization_view_filter_description"),
            to: "/settings/default-employee-view",
            icon: "fa-filter",
            allOfPermissions: [ApiPermission.settings_organization],
          },
          {
            title: i18n.t("core:settings_preboarding_title"),
            description: i18n.t("core:settings_preboarding_description"),
            to: "/settings/preboarding",
            icon: "fa-door-open",
            allOfPermissions: [ApiPermission.settings_organization],
          },
          {
            title: i18n.t("core:settings_home_layout_title"),
            description: i18n.t("core:settings_home_layout_description"),
            to: "/settings/home-layout",
            icon: "fa-table-layout",
            allOfPermissions: [ApiPermission.settings_organization],
          },
        ],
      },
      {
        key: "analytics",
        title: i18n.t("core:settings_analytics_title"),
        description: i18n.t("core:settings_analytics_description"),
        icon: "fa-chart-line",
        items: [
          {
            title: i18n.t("core:settings_analytics_title"),
            description: i18n.t("core:settings_analytics_description"),
            to: "/settings/metrics-configuration",
            icon: "fa-chart-line",
            allOfPermissions: [ApiPermission.settings_organization],
          },
        ],
      },
      {
        key: "company-structure",
        title: i18n.t("core:settings_company_structure_title"),
        description: i18n.t("core:settings_company_structure_description"),
        icon: "fa-sitemap",
        items: [
          {
            title: i18n.t("core:divisions"),
            description: i18n.t("core:settings_division_description"),
            to: "/settings/divisions",
            icon: "fa-folder-tree",
            allOfPermissions: [ApiPermission.settings_divisions],
          },
          {
            title: i18n.t("core:departments"),
            description: i18n.t("core:settings_departments_description"),
            to: "/settings/departments",
            icon: "fa-list-tree",
            allOfPermissions: [ApiPermission.settings_departments],
          },
          {
            title: i18n.t("core:job_profiles"),
            description: i18n.t("core:settings_job_profiles_description"),
            to: "/settings/jobs/framework",
            activePaths: ["/settings/jobs/roles"],
            icon: "fa-circle-user",
            allOfPermissions: [ApiPermission.settings_positions_levels],
          },
          {
            title: i18n.t("common:locations"),
            description: i18n.t("core:settings_locations_description"),
            to: "/settings/locations",
            icon: "fa-location-dot",
            allOfPermissions: [ApiPermission.settings_locations],
          },
          {
            title: i18n.t("core:legal_entities"),
            description: i18n.t("core:settings_legal_entities_description"),
            to: "/settings/legal-entities",
            icon: "fa-building",
            allOfPermissions: [ApiPermission.settings_legal_entities],
          },
        ],
      },
      {
        key: "policies",
        title: i18n.t("core:settings_policies_title"),
        description: i18n.t("core:settings_policies_description"),
        icon: "fa-book-section",
        items: [
          {
            title: i18n.t("time_off:settings_title"),
            description: i18n.t("time_off:settings_description"),
            to: "/settings/time-off",
            icon: "fa-island-tropical",
            allOfPermissions: [ApiPermission.manage_leave_policies],
            planFeature: ApiFeatureType.time_off,
          },
          {
            title: i18n.t("approval:settings_title"),
            description: i18n.t("approval:settings_description"),
            to: "/settings/approval-categories",
            icon: "fa-arrow-progress",
            allOfPermissions: [ApiPermission.manage_approval_policy],
            planFeature: ApiFeatureType.approvals,
          },
          {
            title: i18n.t("core:calendar_holidays_title"),
            description: i18n.t("core:settings_calendar_holidays_description"),
            to: "/settings/holidays",
            icon: "fa-calendar-day",
            allOfPermissions: [ApiPermission.manage_holidays],
          },
          {
            title: i18n.t("core:probation_policies"),
            description: i18n.t("core:settings_probation_policies_description"),
            to: "/settings/probation-policies",
            icon: "fa-user-clock",
            allOfPermissions: [ApiPermission.settings_probation_policies],
          },
        ],
      },
      {
        key: "employment",
        title: i18n.t("core:settings_employment_title"),
        description: i18n.t("core:settings_employment_description"),
        icon: "fa-briefcase",
        items: [
          {
            title: i18n.t("hiring_flow:title"),
            description: i18n.t("core:settings_hiring_flows_description"),
            to: "/settings/hiring-flows",
            icon: "fa-person-from-portal",
            allOfPermissions: [ApiPermission.settings_hiring_flows],
          },
          {
            title: i18n.t("core:termination_leave_types"),
            description: i18n.t("core:settings_termination_leave_types_description"),
            to: "/settings/termination-leave-types",
            icon: "fa-door-open",
            allOfEditFieldPermissions: [EmployeeFieldPermission.manage_employee_termination],
          },
          {
            title: i18n.t("core:termination_reasons"),
            description: i18n.t("core:settings_termination_reasons_description"),
            to: "/settings/termination-reasons",
            icon: "fa-user-minus",
            allOfEditFieldPermissions: [EmployeeFieldPermission.manage_employee_termination],
          },
          {
            title: i18n.t("core:employment_types"),
            description: i18n.t("core:settings_employment_types_description"),
            to: "/settings/employment-types",
            icon: "fa-briefcase",
            allOfPermissions: [ApiPermission.settings_employment_types],
          },
          {
            title: i18n.t("core:employment_contracts"),
            description: i18n.t("core:settings_employment_contracts_description"),
            to: "/settings/employment-contracts",
            icon: "fa-file-contract",
            allOfPermissions: [ApiPermission.settings_employment_contracts],
          },
          {
            title: i18n.t("core:compensation_change_reasons"),
            description: i18n.t("core:settings_compensation_change_reasons_description"),
            to: "/settings/compensation-change-reasons",
            icon: "fa-money-bill",
            allOfPermissions: [ApiPermission.settings_compensation_change_reasons],
          },
          {
            title: i18n.t("core:compensation_types"),
            description: i18n.t("core:settings_compensation_types_description"),
            to: "/settings/compensation-types",
            icon: "fa-coins",
            allOfPermissions: [ApiPermission.settings_compensation_types],
          },
          {
            title: i18n.t("core:employee_position_change_reasons"),
            description: i18n.t("core:settings_employee_position_change_reasons_description"),
            to: "/settings/employee-position-change-reasons",
            icon: "fa-arrow-right-arrow-left",
            allOfEditFieldPermissions: [EmployeeFieldPermission.manage_employee_position],
          },
          {
            title: i18n.t("core:work_patterns"),
            description: i18n.t("core:settings_work_patterns_description"),
            to: "/settings/work-patterns",
            icon: "fa-business-time",
            allOfPermissions: [ApiPermission.settings_work_patterns],
          },
        ],
      },
    ],
  },
  {
    title: i18n.t("core:employee"),
    groups: [
      {
        key: "employee-profile",
        title: i18n.t("core:settings_employee_profile_title"),
        description: i18n.t("core:permissions_custom_fields_description"),
        icon: "fa-id-card-clip",
        items: [
          {
            title: i18n.t("custom_fields:settings_title"),
            description: i18n.t("core:permissions_custom_fields_description"),
            to: "/settings/custom-fields",
            icon: "fa-user",
            allOfPermissions: [ApiPermission.manage_custom_fields],
          },
        ],
      },
      {
        key: "useful-links",
        title: i18n.t("core:organization_links"),
        description: i18n.t("core:settings_organization_links_description"),
        icon: "fa-link",
        items: [
          {
            title: i18n.t("core:organization_links"),
            description: i18n.t("core:settings_organization_links_description"),
            to: "/settings/useful-links",
            icon: "fa-link",
            allOfPermissions: [ApiPermission.settings_organization_links],
          },
        ],
      },
      {
        key: "recognitions",
        title: i18n.t("recognition:settings_title"),
        description: i18n.t("recognition:settings_description"),
        icon: "fa-award",
        items: [
          {
            title: i18n.t("recognition:settings_title"),
            description: i18n.t("recognition:settings_description"),
            to: "/settings/recognitions",
            icon: "fa-award",
            allOfPermissions: [ApiPermission.settings_recognitions],
            planFeature: ApiFeatureType.recognitions,
          },
        ],
      },
    ],
  },
  {
    title: i18n.t("core:settings_recruitment"),
    groups: [
      {
        key: "pipelines",
        title: i18n.t("ats:pipelines"),
        description: i18n.t("core:settings_pipelines_description"),
        icon: "fa-filter-list",
        items: [
          {
            title: i18n.t("ats:pipelines"),
            description: i18n.t("core:settings_pipelines_description"),
            to: "/settings/pipelines",
            icon: "fa-timeline",
            allOfPermissions: [ApiPermission.settings_pipelines, ApiPermission.recruiting_module],
            planFeature: ApiFeatureType.ats,
          },
        ],
      },
      {
        key: "recruitment-directories",
        title: i18n.t("core:settings_recruitment_directories_title"),
        description: i18n.t("core:settings_recruitment_directories_description"),
        icon: "fa-cabinet-filing",
        items: [
          {
            title: i18n.t("ats:sources"),
            description: i18n.t("core:settings_sources_description"),
            to: "/settings/sources",
            icon: "fa-file-import",
            allOfPermissions: [ApiPermission.settings_sources, ApiPermission.recruiting_module],
            planFeature: ApiFeatureType.ats,
          },
          {
            title: i18n.t("ats:tags"),
            description: i18n.t("core:settings_tags_description"),
            to: "/settings/labels",
            icon: "fa-tags",
            allOfPermissions: [ApiPermission.manage_candidates, ApiPermission.recruiting_module],
            planFeature: ApiFeatureType.ats,
          },
          {
            title: i18n.t("ats:disqualification_reasons"),
            description: i18n.t("core:settings_disqualification_reasons_description"),
            to: "/settings/disqualification-reasons",
            icon: "fa-user-xmark",
            allOfPermissions: [
              ApiPermission.settings_candidate_disqualification_reasons,
              ApiPermission.recruiting_module,
            ],
            planFeature: ApiFeatureType.ats,
          },
          {
            title: i18n.t("ats:job_archiving_reasons"),
            description: i18n.t("core:settings_job_archiving_reasons_description"),
            to: "/settings/job-archiving-reasons",
            icon: "fa-box-archive",
            allOfPermissions: [
              ApiPermission.settings_job_archiving_reasons,
              ApiPermission.recruiting_module,
            ],
            planFeature: ApiFeatureType.ats,
          },
        ],
      },
      {
        key: "recruitment-fields",
        title: i18n.t("custom_fields:recruiting_settings_title"),
        description: i18n.t("custom_fields:recruiting_settings_description"),
        icon: "fa-pen-field",
        items: [
          {
            title: i18n.t("custom_fields:applicant_fields"),
            description: i18n.t("custom_fields:applicant_fields_description"),
            to: "/settings/recruitment-fields/applicant",
            icon: "fa-pen-field",
            allOfPermissions: [ApiPermission.manage_custom_fields, ApiPermission.recruiting_module],
            planFeature: ApiFeatureType.ats,
          },
          {
            title: i18n.t("custom_fields:job_fields"),
            description: i18n.t("custom_fields:job_fields_description"),
            to: "/settings/recruitment-fields/job",
            icon: "fa-pen-field",
            allOfPermissions: [ApiPermission.manage_custom_fields, ApiPermission.recruiting_module],
            planFeature: ApiFeatureType.ats,
          },
          {
            title: i18n.t("custom_fields:offer_fields"),
            description: i18n.t("custom_fields:offer_fields_description"),
            to: "/settings/recruitment-fields/offer",
            icon: "fa-pen-field",
            allOfPermissions: [ApiPermission.manage_custom_fields, ApiPermission.recruiting_module],
            planFeature: ApiFeatureType.ats,
          },
        ],
      },
      {
        key: "career-pages",
        title: i18n.t("ats:career_sites"),
        description: i18n.t("ats:career_sites_settings_description"),
        icon: "fa-globe-pointer",
        items: [
          {
            title: i18n.t("ats:career_sites"),
            description: i18n.t("ats:career_sites_settings_description"),
            to: "/settings/career-sites",
            icon: "fa-globe-pointer",
            anyOfPermissions: [ApiPermission.manage_career_sites],
            planFeature: ApiFeatureType.ats_career_site,
          },
        ],
      },
      {
        key: "templates",
        title: i18n.t("common:templates"),
        description: i18n.t("core:settings_templates_description"),
        icon: "fa-message-lines",
        items: [
          {
            title: i18n.t("common:templates"),
            description: i18n.t("core:settings_templates_description"),
            to: "/settings/templates",
            icon: "fa-envelopes",
            anyOfPermissions: [ApiPermission.settings_templates, ApiPermission.manage_candidates],
            planFeature: ApiFeatureType.ats,
          },
        ],
      },
    ],
  },
  {
    title: i18n.t("common:security"),
    groups: [
      {
        key: "roles-and-permissions",
        title: i18n.t("core:roles_and_permissions"),
        description: i18n.t("core:settings_roles_and_permissions_description"),
        icon: "fa-shield-check",
        items: [
          {
            title: i18n.t("core:roles_and_permissions"),
            description: i18n.t("core:settings_roles_and_permissions_description"),
            to: "/settings/roles-and-permissions",
            icon: "fa-shield-check",
            allOfPermissions: [ApiPermission.settings_roles_permissions],
          },
        ],
      },
      {
        key: "data-protection",
        title: i18n.t("data_protection:data_protection"),
        description: i18n.t("data_protection:settings_data_protection_description"),
        icon: "fa-shield-user",
        items: [
          {
            title: i18n.t("data_protection:data_protection"),
            description: i18n.t("data_protection:settings_data_protection_description"),
            to: "/settings/data-protection",
            icon: "fa-user-shield",
            allOfPermissions: [ApiPermission.settings_data_protection],
            planFeature: ApiFeatureType.ats_gdpr,
          },
        ],
      },
      {
        key: "security-settings",
        title: i18n.t("core:settings_security_title"),
        description: i18n.t("core:settings_security_description"),
        icon: "fa-lock",
        items: [
          {
            title: i18n.t("core:settings_security_title"),
            description: i18n.t("core:settings_security_description"),
            to: "/settings/security",
            icon: "fa-face-viewfinder",
            allOfPermissions: [ApiPermission.settings_security],
          },
        ],
      },
    ],
  },
  {
    title: i18n.t("core:settings_platform"),
    groups: [
      {
        key: "import-data",
        title: i18n.t("core:settings_import_data"),
        description: i18n.t("core:settings_import_data_description"),
        icon: "fa-cloud-arrow-down",
        items: [
          {
            title: i18n.t("core:settings_import_data"),
            description: i18n.t("core:settings_import_data_description"),
            to: "/settings/import-data",
            icon: "fa-file-import",
            allOfPermissions: [ApiPermission.data_import],
          },
        ],
      },
      {
        key: "integrations",
        title: i18n.t("integrations:settings_integrations_title"),
        description: i18n.t("integrations:settings_integrations_description"),
        icon: "fa-circle-nodes",
        items: [
          {
            title: i18n.t("integrations:settings_integrations_title"),
            description: i18n.t("integrations:settings_integrations_description"),
            to: "/settings/integrations",
            icon: "fa-gears",
            allOfPermissions: [ApiPermission.settings_integrations],
          },
        ],
      },
      {
        key: "api-tokens",
        title: i18n.t("core:service_users"),
        description: i18n.t("core:service_users_description"),
        icon: "fa-key",
        items: [
          {
            title: i18n.t("core:service_users"),
            description: i18n.t("core:service_users_description"),
            to: "/settings/api-tokens",
            icon: "fa-key",
            allOfPermissions: [ApiPermission.settings_api_users],
          },
        ],
      },
      {
        key: "webhooks",
        title: i18n.t("core:webhooks"),
        description: i18n.t("core:webhooks_description"),
        icon: "fa-webhook",
        items: [
          {
            title: i18n.t("core:webhooks"),
            description: i18n.t("core:webhooks_description"),
            to: "/settings/webhooks",
            icon: "fa-webhook",
            allOfPermissions: [ApiPermission.settings_webhooks],
          },
        ],
      },
    ],
  },
];

export const getSettingsPersonalLinks = (employee: ApiMeEmployee): SettingsPersonalLink[] => [
  {
    title: i18n.t("core:settings_personal_account_title"),
    description: i18n.t("core:settings_personal_account_caption"),
    icon: "fa-at",
    to: "/account-settings",
  },
  {
    title: i18n.t("core:settings_personal_info_title"),
    description: i18n.t("core:settings_personal_info_caption"),
    icon: "fa-user",
    to: `/employees/${employee.id}`,
  },
];

const isItemVisible = (item: SettingsItem, employee: ApiMeEmployee): boolean => {
  const featureEnabled = item.planFeature
    ? employee.plan?.features[item.planFeature]?.enabled
    : true;
  if (!featureEnabled) return false;

  const hasAllRequiredPermissions =
    item.allOfPermissions && item.allOfPermissions.length > 0
      ? item.allOfPermissions.every((p) => employee.api_permissions[p] === PermissionType.edit)
      : true;

  const hasAnyOfPermissions =
    item.anyOfPermissions && item.anyOfPermissions.length > 0
      ? item.anyOfPermissions.some((p) => employee.api_permissions[p] === PermissionType.edit)
      : true;

  const hasAllOfEditFieldPermissions =
    item.allOfEditFieldPermissions && item.allOfEditFieldPermissions.length > 0
      ? item.allOfEditFieldPermissions.every((p) =>
          employee.other_field_permissions_edit.includes(p)
        )
      : true;

  return hasAllRequiredPermissions && hasAnyOfPermissions && hasAllOfEditFieldPermissions;
};

export const getSettingsVisibleToEmployee = (employee: ApiMeEmployee): SettingsCategory[] => {
  const result: SettingsCategory[] = [];

  for (const category of getSettingsNavigation()) {
    const visibleGroups: SettingsGroup[] = [];

    for (const group of category.groups) {
      const visibleItems = group.items.filter((item) => isItemVisible(item, employee));
      if (visibleItems.length > 0) {
        visibleGroups.push({ ...group, items: visibleItems });
      }
    }

    if (visibleGroups.length > 0) {
      result.push({ title: category.title, groups: visibleGroups });
    }
  }

  return result;
};

export const getSettingsGroupLink = (group: SettingsGroup): string =>
  group.items.length === 1 ? group.items[0].to : `/settings/${group.key}`;

export const isSettingsGroupActive = (group: SettingsGroup, pathname: string): boolean => {
  const matchesPath = (path: string) => pathname === path || pathname.startsWith(`${path}/`);
  const landing = `/settings/${group.key}`;
  const onLanding = matchesPath(landing);
  const onItem = group.items.some(
    (item) => matchesPath(item.to) || item.activePaths?.some(matchesPath)
  );
  return onLanding || onItem;
};

export const findSettingsGroup = (
  employee: ApiMeEmployee,
  groupKey: string
): SettingsGroup | undefined => {
  for (const category of getSettingsVisibleToEmployee(employee)) {
    const group = category.groups.find((g) => g.key === groupKey);
    if (group) return group;
  }
  return undefined;
};
